import { APP_ENV, APP_VERSION } from "../env";
import { maskString } from "../masking";

import { metrics } from "../metrics";
import type { EnrichedEvent, IdentifyTraits, PageInfo } from "../types";
import type { Provider } from "./types";



// DSN do Sentry é público por design (SDK roda no browser). Hardcoded por
// limitação atual do hosting (sem env vars de build VITE_*). Pode ser
// sobrescrito via VITE_SENTRY_DSN se disponível no futuro.
const SENTRY_DSN =
  (import.meta.env.VITE_SENTRY_DSN as string | undefined) ||
  "https://1aadba18df3177b0141bbdda43ff8b15@o4511424794656768.ingest.us.sentry.io/4511424809009152";

const IGNORE_ERRORS: ReadonlyArray<string | RegExp> = [
  /ResizeObserver loop/i,
  /ResizeObserver loop completed/i,
  /Non-Error promise rejection captured/i,
  /Network request failed/i,
  /Load failed/i,
  /NetworkError when attempting/i,
  /AbortError: The user aborted a request/i,
  /Failed to fetch dynamically imported module/i,
];

const DENY_URLS: ReadonlyArray<RegExp> = [
  /^chrome-extension:\/\//i,
  /^moz-extension:\/\//i,
  /^safari-extension:\/\//i,
  /^webkit-masked-url:\/\//i,
];

export class SentryProvider implements Provider {
  readonly name = "sentry";
  readonly categories = ["essential"] as const;
  private loaded = false;
  private sentry: typeof import("@sentry/react") | null = null;

  async load(): Promise<void> {
    if (this.loaded) return;
    if (!SENTRY_DSN || APP_ENV === "local" || APP_ENV === "killed") {
      // No DSN in local/dev → skip silently, but mark "loaded" so we don't retry.
      this.loaded = true;
      return;
    }
    const t0 = performance.now();
    try {
      const Sentry = await import("@sentry/react");
      Sentry.init({
        dsn: SENTRY_DSN,
        environment: APP_ENV,
        release: APP_VERSION,
        sendDefaultPii: false,
        // F1 hardening: drop the default BrowserSession integration so Sentry
        // does NOT emit Release Health "session" envelopes (which carry a
        // stable `did` derived from setUser). We keep error envelopes with the
        // hashed correlation id only.
        integrations: (defaults) => defaults.filter((i) => i.name !== "BrowserSession"),

        ignoreErrors: IGNORE_ERRORS as (string | RegExp)[],
        denyUrls: DENY_URLS as RegExp[],
        // Tracing: aggressive in preview, conservative in production.
        tracesSampleRate: APP_ENV === "preview" ? 0.5 : 0.1,
        // Replays only in Sprint 3.
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 0,
        beforeSend(event) {

          // Scrub potentially sensitive request data.
          if (event.request) {
            delete event.request.cookies;
            if (event.request.headers) {
              delete (event.request.headers as Record<string, unknown>).Authorization;
              delete (event.request.headers as Record<string, unknown>).Cookie;
              delete (event.request.headers as Record<string, unknown>)["X-Api-Key"];
              delete (event.request.headers as Record<string, unknown>)["x-api-key"];
            }
            delete (event.request as { data?: unknown }).data;
            delete (event.request as { query_string?: unknown }).query_string;
          }
          // Strip query strings that may carry click-ids and mask PII in URL.
          if (event.request?.url) {
            try {
              const u = new URL(event.request.url);
              ["fbclid", "gclid", "msclkid", "ttclid", "yclid", "token", "email", "cpf", "key", "api_key", "apiKey"].forEach(
                (p) => u.searchParams.delete(p),
              );
              event.request.url = maskString(u.toString());
            } catch {
              event.request.url = maskString(event.request.url);
            }
          }
          // Mask top-level message (captureMessage path).
          if (typeof event.message === "string") {
            event.message = maskString(event.message);
          }
          // Mask exception values (thrown Error messages).
          if (event.exception?.values) {
            for (const v of event.exception.values) {
              if (typeof v.value === "string") v.value = maskString(v.value);
              if (typeof v.type === "string") v.type = maskString(v.type);
            }
          }
          // Recursive deep-scrub helper: masks strings, walks arrays/objects.
          // Safe for trace_id (32 hex no hyphens, not UUID-shaped) and
          // locale/timezone values.
          const scrub = (v: unknown): unknown => {
            if (typeof v === "string") return maskString(v);
            if (Array.isArray(v)) return v.map(scrub);
            if (v && typeof v === "object") {
              const out: Record<string, unknown> = {};
              for (const k of Object.keys(v as Record<string, unknown>)) {
                out[k] = scrub((v as Record<string, unknown>)[k]);
              }
              return out;
            }
            return v;
          };
          // Mask breadcrumb messages + recursively scrub data values.
          if (event.breadcrumbs) {
            for (const b of event.breadcrumbs) {
              if (typeof b.message === "string") b.message = maskString(b.message);
              if (b.data && typeof b.data === "object") {
                b.data = scrub(b.data) as typeof b.data;
              }
            }
          }

          // Transaction name may contain dynamic segments.
          if (typeof event.transaction === "string") {
            event.transaction = maskString(event.transaction);
          }
          // Extra: deep scrub (was previously shallow — nested objects leaked).
          if (event.extra) {
            event.extra = scrub(event.extra) as typeof event.extra;
          }
          // F5 fix: contexts deep scrub. captureException(err, { contexts: { app: ctx } })
          // used to bypass the scrubber and ship raw PII in `contexts.app`.
          // We now deep-walk all contexts; system buckets (trace, react,
          // culture, runtime, browser, os, device) don't contain PII-shaped
          // strings so they pass through unchanged.
          if (event.contexts) {
            event.contexts = scrub(event.contexts) as typeof event.contexts;
          }
          // Tags: shallow scrub string values.
          if (event.tags) {
            for (const k of Object.keys(event.tags)) {
              const val = (event.tags as Record<string, unknown>)[k];
              if (typeof val === "string") {
                (event.tags as Record<string, unknown>)[k] = maskString(val);
              }
            }
          }
          return event;
        },

      });
      this.sentry = Sentry;
      this.loaded = true;
      metrics.providerLoadMs("sentry", Math.round(performance.now() - t0));
    } catch (err) {
      metrics.providerFailure("sentry");
      // Mark loaded to avoid retry loops.
      this.loaded = true;
      // eslint-disable-next-line no-console
      console.warn("[analytics] Sentry load failed:", err);
    }
  }

  isLoaded(): boolean {
    return this.loaded;
  }

  track(event: EnrichedEvent): void {
    if (!this.sentry) return;
    try {
      this.sentry.addBreadcrumb({
        category: "analytics",
        type: "info",
        level: "info",
        message: event.name,
        data: {
          ...event.payload,
          route: event.page_path,
          locale: event.page_locale,
          device: event.device_type,
        },
      });
      if (event.name === "error_boundary_triggered") {
        this.sentry.captureMessage(`error_boundary: ${event.payload.message ?? "unknown"}`, "error");
      }
      if (event.name === "form_submission_failed") {
        this.sentry.captureMessage(
          `form_failed:${event.payload.form_id ?? "?"}:${event.payload.error_code ?? "?"}`,
          "warning",
        );
      }
    } catch {
      metrics.providerFailure("sentry");
    }
  }

  identify(userId: string, traits?: IdentifyTraits): void {
    if (!this.sentry) return;
    try {
      // userId is already hashed at the analytics.identify boundary — never
      // the raw auth UUID. Safe to set as Sentry user.id directly.
      this.sentry.setUser({ id: userId });
      this.sentry.setTag("auth_state", "authenticated");
      if (traits?.provider) this.sentry.setTag("auth_provider", String(traits.provider));
    } catch {
      metrics.providerFailure("sentry");
    }
  }



  page(_page: PageInfo, event: EnrichedEvent): void {
    if (!this.sentry) return;
    try {
      this.sentry.addBreadcrumb({
        category: "navigation",
        message: event.page_path,
        level: "info",
        data: { previous: event.payload.previous_path ?? null },
      });
    } catch {
      metrics.providerFailure("sentry");
    }
  }

  reset(): void {
    if (!this.sentry) return;
    try {
      this.sentry.setUser(null);
      this.sentry.setTag("auth_state", "anonymous");
      this.sentry.setTag("auth_provider", "");
    } catch {
      /* noop */
    }
  }


  // Bridge for ErrorBoundary
  captureException(err: unknown, ctx?: Record<string, unknown>): void {
    if (!this.sentry) return;
    try {
      this.sentry.captureException(err, ctx ? { contexts: { app: ctx } } : undefined);
    } catch {
      /* noop */
    }
  }
}
